import { ExportSpecModel } from 'app/models/dms/exportSpec.model';
import { TaxStatusModel } from 'app/models/dms/taxStatus.model';
import { Injectable } from '@angular/core';
import { LookupInternalModel, LookupExternalModel, CurrencyModel, CountryModel, TaxRatesModel, LookupExtImageModel } from 'app/models/shared/lookup.model';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';

import { CacheService } from './cache.service';

import { LookupInternalCollection, VehicleClass, LookupExternalCollection, TaxStatusEnum, LookupExtImageCollection, EntityType } from 'app/utility/enums';
import { Constants } from 'app/utility/constants';
import { CustomFieldResultModel } from 'app/models/shared/customField.model';

@Injectable({
    providedIn: 'root'
})
export class GeneralService
{
    constructor(private httpClient: HttpClient, private cache: CacheService) { }

    private lastModuleCacheKey: string = 'last-module-name';

    getCachedInternalLookups(collection: LookupInternalCollection, includeNone: boolean = false, noneLabel = "None"): Observable<LookupInternalModel[]>
    {
        let cacheKey: string = 'internal' + collection + includeNone + noneLabel;
        let cacheMilliseconds: number = 1 * 60 * 60000; //1 hour
        let retryIfLengthIsZero = true;
        return this.cache.get(cacheKey, this.getInternalLookups(collection, includeNone, noneLabel), cacheMilliseconds, retryIfLengthIsZero);
    }

    getInternalLookups(collection: LookupInternalCollection, includeNone: boolean = false, noneLabel = "None"): Observable<LookupInternalModel[]>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('includeNone', includeNone);
        httpParams = httpParams.set('noneLabel', noneLabel);
        return this.httpClient.get<LookupInternalModel[]>(Constants.ApiUrl + 'lookups/internal/' + collection, { params: httpParams });
    }

    getCachedExternalLookups(vehicleClass: VehicleClass = VehicleClass.None, collection: LookupExternalCollection, parentId: string = Constants.EmptyGuid, clear: boolean = false, includeNone: boolean = false, noneLabel = "None"): Observable<LookupExternalModel[]>
    {
        let classStr: string = vehicleClass.toString();
        let colStr: string = collection.toString();
        if (!parentId)
        {
            parentId = '';
        }

        let cacheKey: string = 'external' + classStr + colStr + parentId + includeNone + noneLabel;
        // console.log(cacheKey);
        let cacheMilliseconds: number = 1 * 60 * 60000; //1 hour
        let retryIfLengthIsZero = true;
        if (clear)
        {
            this.cache.clear(cacheKey);
        }
        return this.cache.get(cacheKey, this.getExternalLookups(vehicleClass, collection, parentId, includeNone, noneLabel), cacheMilliseconds, retryIfLengthIsZero);
    }

    getExternalLookups(vehicleClass: VehicleClass = VehicleClass.None, collection: LookupExternalCollection, parentId: string, includeNone: boolean = false, noneLabel = "None"): Observable<LookupExternalModel[]>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('vehicleClass', vehicleClass.toString());
        if (parentId)
        {
            httpParams = httpParams.append('parentId', parentId);
        }
        httpParams = httpParams.append('includeNone', includeNone);
        httpParams = httpParams.append('noneLabel', noneLabel);
        return this.httpClient.get<LookupExternalModel[]>(Constants.ApiUrl + 'lookups/external/' + collection, { params: httpParams });
    }

    getCachedExtImageLookups(collection: LookupExtImageCollection, parentId: string = Constants.EmptyGuid, clear: boolean = false): Observable<LookupExtImageModel[]>
    {
        let colStr: string = collection.toString();
        if (!parentId)
        {
            parentId = '';
        }

        let cacheKey: string = 'extimage' + colStr + parentId;
        let cacheMilliseconds: number = 1 * 60 * 60000;
        let retryIfLengthIsZero = true;
        if (clear)
        {
            this.cache.clear(cacheKey);
        }
        return this.cache.get(cacheKey, this.getExtImageLookups(collection, parentId), cacheMilliseconds, retryIfLengthIsZero);
    }

    getExtImageLookups(collection: LookupExtImageCollection, parentId: string): Observable<LookupExtImageModel[]>
    {
        let httpParams = new HttpParams();
        if (parentId)
        {
            httpParams = httpParams.append('parentId', parentId);
        }
        return this.httpClient.get<LookupExtImageModel[]>(Constants.ApiUrl + 'lookups/extimage/' + collection, { params: httpParams });
    }

    findOrCreateExternalLookupCached(caption: string, vehicleClass: VehicleClass = VehicleClass.None, collection: LookupExternalCollection, parentId: string, code: string = '', reference: string = '', customField1: string = ''): Observable<LookupExternalModel>
    {
        let cacheKey: string = 'external' + caption + vehicleClass.toString() + collection + parentId;
        let cacheMilliseconds: number = 1 * 60 * 60000; //1 hour
        let retryIfLengthIsZero = true;
        return this.cache.get(cacheKey, this.findOrCreateExternalLookup(caption, vehicleClass, collection, parentId, code, reference, customField1), cacheMilliseconds, retryIfLengthIsZero);
    }

    findOrCreateExternalLookup(caption: string, vehicleClass: VehicleClass = VehicleClass.None, collection: LookupExternalCollection, parentId: string, code: string = '', reference: string = '', customField1: string = '', description: string = ''): Observable<LookupExternalModel>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('caption', caption);
        httpParams = httpParams.set('vehicleClass', vehicleClass.toString());
        if (parentId)
        {
            httpParams = httpParams.append('parentId', parentId);
        }
        if (code)
        {
            httpParams = httpParams.append('code', code);
        }
        if (reference)
        {
            httpParams = httpParams.append('reference', reference);
        }
        if (customField1)
        {
            httpParams = httpParams.append('customField1', customField1);
        }
        if (description)
        {
            httpParams = httpParams.append('description', description);
        }
        return this.httpClient.put<LookupExternalModel>(Constants.ApiUrl + 'lookups/external/' + collection, null, { params: httpParams });
    }

    getCachedBaseCurrency(): Observable<CurrencyModel>
    {
        let cacheKey: string = 'basecurrency';
        let cacheMilliseconds: number = 12 * 60 * 60000; //12 hours
        return this.cache.get(cacheKey, this.getBaseCurrency(), cacheMilliseconds);
    }

    getBaseCurrency(): Observable<CurrencyModel>
    {
        return this.httpClient.get<CurrencyModel>(Constants.ApiUrl + 'lookups/currency/base');
    }

    getImage(imgSrc: string): Observable<Blob>
    {
        return this.httpClient.get(imgSrc, { responseType: "blob" });
    }

    getCurrencies(): Observable<CurrencyModel[]>
    {
        return this.httpClient.get<CurrencyModel[]>(Constants.ApiUrl + 'lookups/currencies');
    }

    getCachedCurrencies(): Observable<CurrencyModel[]>
    {
        let cacheKey: string = 'currencies';
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getCurrencies(), cacheMilliseconds);
    }

    getTaxStatuses(simple: boolean = false): Observable<TaxStatusModel[]>
    {
        let httpParams = new HttpParams();
        // httpParams = httpParams.set('statusEnum', statusEnum.toString());
        httpParams = httpParams.set('simple', simple ? "true" : "false");
        return this.httpClient.get<TaxStatusModel[]>(Constants.ApiUrl + 'lookups/TaxStatuses', { params: httpParams });
    }

    getCachedTaxStatuses(simple: boolean = false): Observable<TaxStatusModel[]>
    {
        let cacheKey: string = 'taxstatuses' + simple;
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getTaxStatuses(simple), cacheMilliseconds);
    }

    getCountries(): Observable<CountryModel[]>
    {
        return this.httpClient.get<CountryModel[]>(Constants.ApiUrl + 'lookups/countries');
    }

    getCachedCountries(clearCache = false): Observable<CountryModel[]>
    {

        let cacheKey: string = 'countries';
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour

        if (clearCache)
        {
            this.cache.clear(cacheKey);
            // console.log('clearing key', cacheKey)
        }

        return this.cache.get(cacheKey, this.getCountries(), cacheMilliseconds);
    }

    saveCountry(country: CountryModel): Observable<CountryModel>
    {
        var params = new HttpParams();
        params = params.set('name', country.name);
        params = params.set('code', country.code);
        //post name and code to api, return country object
        return this.httpClient.post<CountryModel>(Constants.ApiUrl + 'lookups/countries/new', null, { params: params });
    }

    getTaxRates(): Observable<TaxRatesModel>
    {
        return this.httpClient.get<TaxRatesModel>(Constants.ApiUrl + 'lookups/TaxRates');
    }

    getCachedTaxRates(): Observable<TaxRatesModel>
    {
        let cacheKey: string = 'taxrates';
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getTaxRates(), cacheMilliseconds);
    }

    setLastModule(module: string): void
    {
        if (localStorage)
        {
            localStorage.setItem(this.lastModuleCacheKey, module);
        }
    }

    getLastModule(): string
    {
        if (localStorage)
        {
            return localStorage.getItem(this.lastModuleCacheKey);
        }
        else
        {
            return null;
        }
    }

    getCachedCustomFields(entityType: EntityType): Observable<CustomFieldResultModel[]>
    {
        let cacheKey: string = 'customfields-' + entityType;
        let cacheMilliseconds: number = 12 * 60 * 60000; //1 hour
        return this.cache.get(cacheKey, this.getCustomFields(entityType), cacheMilliseconds);
    }

    getCustomFields(entityType: EntityType): Observable<CustomFieldResultModel[]>
    {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('entityType', entityType.toString());
        return this.httpClient.get<CustomFieldResultModel[]>(Constants.ApiUrl + 'lookups/customfields', { params: httpParams });
    }

}
